import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";

const CROSSFADE_MS = 800;

/**
 * Two stacked video elements; crossfades when `src` changes.
 * `videoRef` always points at the currently visible layer.
 */
const CrossfadeVideo = ({
  src,
  muted = true,
  className = "",
  style,
  onClick,
  videoRef,
  loop = true,
  playsInline = true,
  preload,
  id,
  onLoadedData,
  onLoadedMetadata,
  onCanPlay,
}) => {
  const refA = useRef(null);
  const refB = useRef(null);
  const fadeTimerRef = useRef(null);
  const frontIndexRef = useRef(0);

  const [srcA, setSrcA] = useState(src || null);
  const [srcB, setSrcB] = useState(null);
  const [frontIndex, setFrontIndex] = useState(0);
  const [opA, setOpA] = useState(1);
  const [opB, setOpB] = useState(0);

  frontIndexRef.current = frontIndex;

  const syncFrontRef = useCallback(() => {
    const frontEl = frontIndexRef.current === 0 ? refA.current : refB.current;
    if (!videoRef) return;
    if (typeof videoRef === "function") {
      videoRef(frontEl);
    } else {
      videoRef.current = frontEl;
    }
  }, [videoRef]);

  useLayoutEffect(() => {
    syncFrontRef();
  }, [frontIndex, srcA, srcB, syncFrontRef]);

  useEffect(() => {
    if (refA.current) refA.current.muted = muted;
    if (refB.current) refB.current.muted = muted;
  }, [muted]);

  const beginCrossfade = useCallback(
    (backIndex) => {
      const frontEl = frontIndexRef.current === 0 ? refA.current : refB.current;
      const backEl = backIndex === 0 ? refA.current : refB.current;
      if (!backEl) return;

      if (frontEl) frontEl.muted = true;
      backEl.muted = muted;
      const p = backEl.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {});
      }

      if (backIndex === 0) {
        setOpA(1);
        setOpB(0);
      } else {
        setOpB(1);
        setOpA(0);
      }

      const duration =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? 0
          : CROSSFADE_MS;

      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = setTimeout(() => {
        setFrontIndex(backIndex);
        frontIndexRef.current = backIndex;
        if (backIndex === 0) {
          setSrcB(null);
          setOpA(1);
          setOpB(0);
        } else {
          setSrcA(null);
          setOpB(1);
          setOpA(0);
        }
        syncFrontRef();
        fadeTimerRef.current = null;
      }, duration);
    },
    [muted, syncFrontRef]
  );

  const handleLayerCanPlay = useCallback(
    (layerIndex) => {
      const backIndex = 1 - frontIndexRef.current;
      if (layerIndex !== backIndex) return;
      const backSrc = backIndex === 0 ? srcA : srcB;
      if (!backSrc || backSrc !== src) return;
      beginCrossfade(backIndex);
    },
    [src, srcA, srcB, beginCrossfade]
  );

  useEffect(() => {
    if (!src) return undefined;

    const frontSrc = frontIndex === 0 ? srcA : srcB;
    if (src === frontSrc) return undefined;

    if (!frontSrc) {
      if (frontIndex === 0) setSrcA(src);
      else setSrcB(src);
      return undefined;
    }

    const backIndex = 1 - frontIndex;
    if (backIndex === 0) setSrcA(src);
    else setSrcB(src);

    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [src, frontIndex, srcA, srcB]);

  useEffect(
    () => () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    },
    []
  );

  const layerStyle = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
    ...style,
  };

  const handleLoadedData = (layerIndex) => (e) => {
    const el = e.currentTarget;
    el.muted = muted;
    if (layerIndex === frontIndexRef.current && el.paused) {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {});
      }
    }
    if (layerIndex === frontIndexRef.current && onLoadedData) {
      onLoadedData(e);
    }
  };

  const handleLoadedMetadata = (layerIndex) => (e) => {
    if (layerIndex === frontIndexRef.current && onLoadedMetadata) {
      onLoadedMetadata(e);
    }
  };

  const handleCanPlay = (layerIndex) => (e) => {
    handleLayerCanPlay(layerIndex);
    if (layerIndex === frontIndexRef.current && onCanPlay) {
      onCanPlay(e);
    }
  };

  return (
    <div
      className="weather-video-stack"
      style={{ position: "relative", width: "100%", height: "100%" }}
      onClick={onClick}
    >
      {srcA ? (
        <video
          ref={refA}
          src={srcA}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          preload={preload}
          className={`${className} weather-video-layer`.trim()}
          id={frontIndex === 0 ? id : undefined}
          style={{ ...layerStyle, opacity: opA, zIndex: opA >= opB ? 2 : 1 }}
          onLoadedData={handleLoadedData(0)}
          onLoadedMetadata={handleLoadedMetadata(0)}
          onCanPlay={handleCanPlay(0)}
        />
      ) : null}
      {srcB ? (
        <video
          ref={refB}
          src={srcB}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          preload={preload}
          className={`${className} weather-video-layer`.trim()}
          id={frontIndex === 1 ? id : undefined}
          style={{ ...layerStyle, opacity: opB, zIndex: opB >= opA ? 2 : 1 }}
          onLoadedData={handleLoadedData(1)}
          onLoadedMetadata={handleLoadedMetadata(1)}
          onCanPlay={handleCanPlay(1)}
        />
      ) : null}
    </div>
  );
};

export default CrossfadeVideo;
